import { api } from "./client";

export type ContractStatus =
  | "draft"
  | "internal_review"
  | "with_counterparty"
  | "negotiating"
  | "out_for_signature"
  | "signed"
  | "expired"
  | "terminated"
  | "cancelled";
export type ContractKind =
  | "nda"
  | "msa"
  | "sow"
  | "order_form"
  | "mou"
  | "dpa"
  | "amendment"
  | "other";
export type ContractSide = "us" | "them";
export type ContractGroup = "open" | "signed" | "ended" | "all";

export interface ContractVersion {
  id: string;
  version: number;
  filename: string;
  content_type: string;
  size_bytes: number;
  note: string | null;
  uploaded_by: string | null;
  uploaded_by_name: string | null;
  created_at: string;
}

export interface ContractEvent {
  id: string;
  kind: "created" | "comment" | "status" | "version" | "terms" | "milestone";
  side: ContractSide | null;
  body: string | null;
  meta: {
    from?: ContractStatus;
    to?: ContractStatus;
    status?: ContractStatus;
    version?: number;
    version_id?: string;
    filename?: string;
    changes?: { field: string; from: string; to: string }[];
  } | null;
  author_user_id: string | null;
  author_name: string | null;
  created_at: string;
}

export interface ContractMilestone {
  id: string;
  title: string;
  due_date: string | null;
  done_at: string | null;
  created_at: string;
}

export interface Contract {
  id: string;
  title: string;
  kind: ContractKind;
  status: ContractStatus;
  account_id: string | null;
  account_name: string | null;
  deal_id: string | null;
  deal_lead: string | null;
  owner_user_id: string | null;
  owner_name: string | null;
  counterparty_signer: string | null;
  value_eur: number | null;
  start_date: string | null;
  end_date: string | null;
  auto_renew: boolean;
  notice_days: number | null;
  signed_on: string | null;
  summary: string | null;
  last_event_at: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  waiting_on: ContractSide | null;
  notice_by: string | null;
  latest_version: ContractVersion | null;
  last_event: ContractEvent | null;
  open_milestones: number;
  can_delete: boolean;
}

export interface ContractDetail extends Contract {
  versions: ContractVersion[];
  events: ContractEvent[];
  milestones: ContractMilestone[];
}

export interface ContractUpcoming {
  contract_id: string;
  contract_title: string;
  account_name: string | null;
  kind: "end" | "notice" | "milestone";
  label: string;
  on: string;
  milestone_id: string | null;
}

export type ContractFields = Partial<{
  title: string;
  kind: ContractKind;
  status: ContractStatus;
  account_id: string | null;
  deal_id: string | null;
  owner_user_id: string | null;
  counterparty_signer: string | null;
  value_eur: number | null;
  start_date: string | null;
  end_date: string | null;
  auto_renew: boolean;
  notice_days: number | null;
  signed_on: string | null;
  summary: string | null;
}>;

const base = (id: string) => `/contracts/${id}`;

export const Contracts = {
  list: (params?: { group?: ContractGroup; account_id?: string; deal_id?: string; q?: string }) =>
    api.get<Contract[]>("/contracts", { params }).then((r) => r.data),
  upcoming: () => api.get<ContractUpcoming[]>("/contracts/upcoming").then((r) => r.data),
  get: (id: string) => api.get<ContractDetail>(base(id)).then((r) => r.data),
  create: (payload: ContractFields & { title: string }) =>
    api.post<ContractDetail>("/contracts", payload).then((r) => r.data),
  update: (id: string, payload: ContractFields) =>
    api.patch<ContractDetail>(base(id), payload).then((r) => r.data),
  remove: (id: string) => api.delete(base(id)),
  comment: (id: string, body: string, side: ContractSide) =>
    api.post<ContractDetail>(`${base(id)}/comments`, { body, side }).then((r) => r.data),
  removeComment: (id: string, eventId: string) => api.delete(`${base(id)}/comments/${eventId}`),
  addMilestone: (id: string, title: string, due_date: string | null) =>
    api.post<ContractDetail>(`${base(id)}/milestones`, { title, due_date }).then((r) => r.data),
  updateMilestone: (
    id: string,
    milestoneId: string,
    payload: { title?: string; due_date?: string | null; done?: boolean },
  ) =>
    api.patch<ContractDetail>(`${base(id)}/milestones/${milestoneId}`, payload).then((r) => r.data),
  removeMilestone: (id: string, milestoneId: string) =>
    api.delete(`${base(id)}/milestones/${milestoneId}`),
  uploadVersion: (id: string, file: File, note: string, onProgress?: (f: number) => void) => {
    const form = new FormData();
    form.append("file", file);
    if (note) form.append("note", note);
    return api
      .post<ContractDetail>(`${base(id)}/versions`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => e.total && onProgress?.(e.loaded / e.total),
      })
      .then((r) => r.data);
  },
  /** A short-lived URL a viewer (iframe) or download link can load without headers. */
  versionLink: (id: string, versionId: string) =>
    api
      .get<{ url: string; expires_in: number }>(`${base(id)}/versions/${versionId}/link`)
      .then((r) => r.data.url),
};

export const MAX_CONTRACT_BYTES = 50 * 1024 * 1024;
